/**
 * Offline Storage Module
 *
 * Provides IndexedDB-based offline storage for Draft Slot:
 * - Queue selections made while offline
 * - Trigger background sync when online
 * - Cache entry data for offline viewing
 *
 * Usage:
 *   import { queueSelection, getQueuedSelections, clearQueue } from './offline-storage';
 *
 *   // Queue a selection while offline
 *   await queueSelection(entryId, selectionData);
 *
 *   // Trigger background sync
 *   await requestBackgroundSync();
 */

import { captureNetworkError, captureClientError } from './utils/capture-error';

const DB_NAME = 'draft-slot-offline';
const DB_VERSION = 1;

// Object store names
const STORES = {
  PENDING_SELECTIONS: 'pending-selections',
  CACHED_ENTRIES: 'cached-entries',
};

/** @type {IDBDatabase|null} */
let dbInstance = null;

// ============================================================================
// localStorage Fallback (when IndexedDB is blocked, e.g. Safari private mode)
// ============================================================================

let fallbackMode = false;
let localStorageAvailable = false;

try {
  const key = '__ds_test__';
  localStorage.setItem(key, '1');
  localStorage.removeItem(key);
  localStorageAvailable = true;
} catch (e) {
  if (e?.name === 'SecurityError') {
    fallbackMode = true;
  }
}

const LS_PENDING_KEY = 'ds-pending-selections';
const LS_ENTRIES_KEY = 'ds-cached-entries';
const LS_COUNTER_KEY = 'ds-id-counter';

/**
 * Read and parse a JSON value from localStorage.
 * @param {string} key
 * @returns {any}
 */
function lsRead(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Write a JSON value to localStorage.
 * @param {string} key
 * @param {any} data
 */
function lsWrite(key, data) {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch {
    // Quota exceeded or storage disabled
  }
}

/**
 * Get the next auto-incrementing ID for localStorage fallback.
 * @returns {number}
 */
function lsNextId() {
  const current = lsRead(LS_COUNTER_KEY) || 0;
  const next = current + 1;
  lsWrite(LS_COUNTER_KEY, next);
  return next;
}

// ============================================================================
// Database Initialization
// ============================================================================

/**
 * Open the IndexedDB database.
 * Creates object stores on first open or version upgrade.
 * @returns {Promise<IDBDatabase>}
 */
export function openDatabase() {
  return new Promise((resolve, reject) => {
    // Return cached instance if available
    if (dbInstance) {
      resolve(dbInstance);
      return;
    }

    if (typeof indexedDB === 'undefined') {
      fallbackMode = true;
      reject(new DOMException('IndexedDB is not available', 'SecurityError'));
      return;
    }

    let request;
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (error) {
      fallbackMode = true;
      reject(error);
      return;
    }

    request.onerror = (event) => {
      event.preventDefault();
      fallbackMode = true;
      reject(request.error);
    };

    request.onsuccess = () => {
      dbInstance = request.result;

      // Handle database close (e.g., when user clears data)
      dbInstance.onclose = () => {
        dbInstance = null;
      };

      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Create pending-selections store
      if (!db.objectStoreNames.contains(STORES.PENDING_SELECTIONS)) {
        const store = db.createObjectStore(STORES.PENDING_SELECTIONS, {
          keyPath: 'id',
          autoIncrement: true,
        });

        // Index for querying by entry
        store.createIndex('entryId', 'entryId', { unique: false });
        store.createIndex('timestamp', 'timestamp', { unique: false });
      }

      // Create cached-entries store
      if (!db.objectStoreNames.contains(STORES.CACHED_ENTRIES)) {
        db.createObjectStore(STORES.CACHED_ENTRIES, {
          keyPath: 'id',
        });
      }
    };
  });
}

/**
 * Close the database connection.
 */
export function closeDatabase() {
  if (dbInstance) {
    dbInstance.close();
    dbInstance = null;
  }
}

// ============================================================================
// Pending Selections (Background Sync Queue)
// ============================================================================

/**
 * Queue a selection to be synced when online.
 *
 * @param {number} entryId - The entry ID
 * @param {object} selectionData - The selection data (pick number, player ID, team ID)
 * @returns {Promise<number>} - The ID of the queued selection
 */
export async function queueSelection(entryId, selectionData) {
  if (fallbackMode) {
    if (!localStorageAvailable) return -1;
    const items = lsRead(LS_PENDING_KEY) || [];
    const id = lsNextId();
    items.push({ id, entryId, data: selectionData, timestamp: Date.now() });
    lsWrite(LS_PENDING_KEY, items);
    return id;
  }

  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.PENDING_SELECTIONS, 'readwrite');
    const store = tx.objectStore(STORES.PENDING_SELECTIONS);

    const item = {
      entryId,
      data: selectionData,
      timestamp: Date.now(),
    };

    const request = store.add(item);

    request.onsuccess = () => {
      console.log('[OfflineStorage] Selection queued:', request.result);
      resolve(request.result);
    };

    request.onerror = () => {
      console.error(
        '[OfflineStorage] Failed to queue selection:',
        request.error
      );
      reject(request.error);
    };

    // Request background sync after queueing
    tx.oncomplete = () => {
      requestBackgroundSync().catch((e) => {
        console.warn('[OfflineStorage] Could not request background sync:', e);
      });
    };
  });
}

/**
 * Get all queued selections.
 * @returns {Promise<Array<{id: number, entryId: number, data: object, timestamp: number}>>}
 */
export async function getQueuedSelections() {
  if (fallbackMode) {
    if (!localStorageAvailable) return [];
    return lsRead(LS_PENDING_KEY) || [];
  }

  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.PENDING_SELECTIONS, 'readonly');
    const store = tx.objectStore(STORES.PENDING_SELECTIONS);
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get queued selections for a specific entry.
 * @param {number} entryId
 * @returns {Promise<Array>}
 */
export async function getQueuedSelectionsForEntry(entryId) {
  if (fallbackMode) {
    if (!localStorageAvailable) return [];
    const items = lsRead(LS_PENDING_KEY) || [];
    return items.filter((item) => item.entryId === entryId);
  }

  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.PENDING_SELECTIONS, 'readonly');
    const store = tx.objectStore(STORES.PENDING_SELECTIONS);
    const index = store.index('entryId');
    const request = index.getAll(entryId);

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Remove a selection from the queue (after successful sync).
 * @param {number} id - The queue item ID
 * @returns {Promise<void>}
 */
export async function removeQueuedSelection(id) {
  if (fallbackMode) {
    if (!localStorageAvailable) return;
    const items = lsRead(LS_PENDING_KEY) || [];
    lsWrite(
      LS_PENDING_KEY,
      items.filter((item) => item.id !== id)
    );
    return;
  }

  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.PENDING_SELECTIONS, 'readwrite');
    const store = tx.objectStore(STORES.PENDING_SELECTIONS);
    const request = store.delete(id);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Clear all queued selections.
 * @returns {Promise<void>}
 */
export async function clearQueue() {
  if (fallbackMode) {
    if (localStorageAvailable) localStorage.removeItem(LS_PENDING_KEY);
    return;
  }

  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.PENDING_SELECTIONS, 'readwrite');
    const store = tx.objectStore(STORES.PENDING_SELECTIONS);
    const request = store.clear();

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get the count of queued selections.
 * @returns {Promise<number>}
 */
export async function getQueueCount() {
  if (fallbackMode) {
    if (!localStorageAvailable) return 0;
    return (lsRead(LS_PENDING_KEY) || []).length;
  }

  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.PENDING_SELECTIONS, 'readonly');
    const store = tx.objectStore(STORES.PENDING_SELECTIONS);
    const request = store.count();

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// ============================================================================
// Entry Caching (for offline viewing)
// ============================================================================

/**
 * Cache an entry for offline viewing.
 * @param {object} entry - The entry data to cache
 * @returns {Promise<void>}
 */
export async function cacheEntry(entry) {
  if (fallbackMode) {
    if (!localStorageAvailable) return;
    const entries = lsRead(LS_ENTRIES_KEY) || {};
    entries[entry.id] = { ...entry, cachedAt: Date.now() };
    lsWrite(LS_ENTRIES_KEY, entries);
    return;
  }

  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.CACHED_ENTRIES, 'readwrite');
    const store = tx.objectStore(STORES.CACHED_ENTRIES);

    const item = {
      ...entry,
      cachedAt: Date.now(),
    };

    const request = store.put(item);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get a cached entry by ID.
 * @param {number} id - The entry ID
 * @returns {Promise<object|null>}
 */
export async function getCachedEntry(id) {
  if (fallbackMode) {
    if (!localStorageAvailable) return null;
    const entries = lsRead(LS_ENTRIES_KEY) || {};
    return entries[id] || null;
  }

  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.CACHED_ENTRIES, 'readonly');
    const store = tx.objectStore(STORES.CACHED_ENTRIES);
    const request = store.get(id);

    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get all cached entries.
 * @returns {Promise<Array>}
 */
export async function getAllCachedEntries() {
  if (fallbackMode) {
    if (!localStorageAvailable) return [];
    const entries = lsRead(LS_ENTRIES_KEY) || {};
    return Object.values(entries);
  }

  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.CACHED_ENTRIES, 'readonly');
    const store = tx.objectStore(STORES.CACHED_ENTRIES);
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Remove a cached entry.
 * @param {number} id
 * @returns {Promise<void>}
 */
export async function removeCachedEntry(id) {
  if (fallbackMode) {
    if (!localStorageAvailable) return;
    const entries = lsRead(LS_ENTRIES_KEY) || {};
    delete entries[id];
    lsWrite(LS_ENTRIES_KEY, entries);
    return;
  }

  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.CACHED_ENTRIES, 'readwrite');
    const store = tx.objectStore(STORES.CACHED_ENTRIES);
    const request = store.delete(id);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Clear all cached entries older than a given age.
 * @param {number} maxAgeMs - Maximum age in milliseconds (default: 7 days)
 * @returns {Promise<number>} - Number of entries removed
 */
export async function clearOldCachedEntries(
  maxAgeMs = 7 * 24 * 60 * 60 * 1000
) {
  if (fallbackMode) {
    if (!localStorageAvailable) return 0;
    const entries = lsRead(LS_ENTRIES_KEY) || {};
    const cutoff = Date.now() - maxAgeMs;
    let removed = 0;
    for (const [id, entry] of Object.entries(entries)) {
      if (entry.cachedAt < cutoff) {
        delete entries[id];
        removed++;
      }
    }
    lsWrite(LS_ENTRIES_KEY, entries);
    return removed;
  }

  const db = await openDatabase();
  const cutoff = Date.now() - maxAgeMs;
  let removed = 0;

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.CACHED_ENTRIES, 'readwrite');
    const store = tx.objectStore(STORES.CACHED_ENTRIES);
    const request = store.openCursor();

    request.onsuccess = (event) => {
      const cursor = event.target.result;

      if (cursor) {
        if (cursor.value.cachedAt < cutoff) {
          cursor.delete();
          removed++;
        }
        cursor.continue();
      }
    };

    tx.oncomplete = () => resolve(removed);
    tx.onerror = () => reject(tx.error);
  });
}

// ============================================================================
// Background Sync
// ============================================================================

/**
 * Request a background sync for pending selections.
 * This tells the service worker to sync data when the device is online.
 * @returns {Promise<void>}
 */
export async function requestBackgroundSync() {
  if (
    !('serviceWorker' in navigator) ||
    !('sync' in ServiceWorkerRegistration.prototype)
  ) {
    console.warn('[OfflineStorage] Background sync not supported');
    // Fall back to immediate sync if online
    if (navigator.onLine) {
      return syncPendingSelectionsNow();
    }
    return;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    await registration.sync.register('sync-selections');
    console.log('[OfflineStorage] Background sync registered');
  } catch (error) {
    captureClientError(error, {
      component: 'offline-storage',
      action: 'register-background-sync',
    });
    console.error(
      '[OfflineStorage] Failed to register background sync:',
      error
    );
    // Fall back to immediate sync if online
    if (navigator.onLine) {
      return syncPendingSelectionsNow();
    }
  }
}

/**
 * Immediately sync all pending selections (fallback when background sync isn't available).
 * @returns {Promise<{success: number, failed: number}>}
 */
export async function syncPendingSelectionsNow() {
  const pending = await getQueuedSelections();
  let success = 0;
  let failed = 0;

  for (const selection of pending) {
    try {
      const response = await fetch(
        `/api/entries/${selection.entryId}/selections`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-CSRF-TOKEN': getCSRFToken(),
          },
          credentials: 'include',
          body: JSON.stringify(selection.data),
        }
      );

      if (response.ok) {
        await removeQueuedSelection(selection.id);
        success++;
        console.log('[OfflineStorage] Synced selection:', selection.id);
      } else {
        failed++;
        console.error(
          '[OfflineStorage] Failed to sync selection:',
          response.status
        );
      }
    } catch (error) {
      failed++;
      captureNetworkError(error, {
        component: 'offline-storage',
        entryId: selection.entryId,
      });
      console.error('[OfflineStorage] Error syncing selection:', error);
    }
  }

  return { success, failed };
}

/**
 * Get the CSRF token from the meta tag.
 * @returns {string}
 */
function getCSRFToken() {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.getAttribute('content') : '';
}

// ============================================================================
// Online/Offline Status
// ============================================================================

/**
 * Check if the device is online.
 * @returns {boolean}
 */
export function isOnline() {
  return navigator.onLine;
}

/**
 * Listen for online status changes.
 * @param {(online: boolean) => void} callback
 * @returns {() => void} - Cleanup function to remove listeners
 */
export function onOnlineStatusChange(callback) {
  const handleOnline = () => callback(true);
  const handleOffline = () => callback(false);

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  };
}

// ============================================================================
// Alpine.js Integration (DEPRECATED - Use $store.app instead)
// ============================================================================

/**
 * @deprecated Use $store.app instead for global offline/sync state.
 *
 * This data component is maintained for backwards compatibility but delegates
 * to the app store. New code should use:
 *
 *   $store.app.isOnline
 *   $store.app.pendingSyncCount
 *   $store.app.syncing
 *   $store.app.requestSync()
 *
 * Usage in Blade (deprecated):
 *   <div x-data="offlineStorage">
 *     <span x-show="pendingCount > 0">
 *       <span x-text="pendingCount"></span> pending changes
 *     </span>
 *   </div>
 */
export function createOfflineStorageComponent() {
  return {
    // Proxy getters to store
    get isOnline() {
      return window.Alpine?.store('app')?.isOnline ?? navigator.onLine;
    },

    get pendingCount() {
      return window.Alpine?.store('app')?.pendingSyncCount ?? 0;
    },

    get syncing() {
      return window.Alpine?.store('app')?.syncing ?? false;
    },

    init() {
      console.warn(
        '[offlineStorage] DEPRECATED: x-data="offlineStorage" is deprecated. ' +
          'Use $store.app instead for global offline/sync state.'
      );
    },

    destroy() {
      // No cleanup needed - store handles it
    },

    async updatePendingCount() {
      console.warn(
        '[offlineStorage] DEPRECATED: Use $store.app.pendingSyncCount instead'
      );
      // Store updates automatically
    },

    async syncNow() {
      console.warn(
        '[offlineStorage] DEPRECATED: Use $store.app.requestSync() instead'
      );
      const store = window.Alpine?.store('app');
      if (store) {
        await store.requestSync();
        this.$dispatch('offline-sync-complete', {});
      }
    },
  };
}

// ============================================================================
// Test Helpers (only used in tests)
// ============================================================================

/**
 * Force fallback mode on/off. For testing only.
 * @param {boolean} enabled
 */
export function _setFallbackMode(enabled) {
  fallbackMode = enabled;
}

/**
 * Check if fallback mode is active.
 * @returns {boolean}
 */
export function _isFallbackMode() {
  return fallbackMode;
}

// Register with Alpine when available
if (typeof window !== 'undefined') {
  document.addEventListener('alpine:init', () => {
    window.Alpine.data('offlineStorage', createOfflineStorageComponent);
  });
}
